// cashierATransactions.js — Régie (state service) collection desk.
//
// Rewired onto the money engine (spec §13 step 4). What changed, and why:
//
//  • The `supabase.from('transactions').insert()` + `generateReceiptNumber()` path
//    is GONE. Receipt numbers were `REC-YYYYMMDD-` + `Math.random()*10000`: 10,000
//    slots drawn randomly per prefix per day, a 39% chance of a collision at 100
//    receipts/day (audit C1). Numbers now come from a pre-allocated per-device
//    block a device can prove it owns (invariant 3).
//
//  • THE "ANY INSERT ERROR MEANS WE ARE OFFLINE" BRANCH IS DELETED. That branch is
//    C1's root: a unique-constraint violation was read as "we must be offline",
//    stashed locally, shown as a GREEN « Transaction saved offline », and printed.
//    A refusal is a refusal and is now shown as one, with the exact French message
//    for the rule that fired. Being offline is not an error at all — the write is
//    local first, always, and the outbox drains when the network returns.
//
//  • The cart is CLEARED on success and the receipt modal is non-dismissible
//    except via « Nouvelle Transaction » (audit H3: dismissing via ✕ or the
//    backdrop left the cart loaded, and one more click booked a second identical
//    sale with a new receipt number).
//
//  • Every post-commit side effect is OUTSIDE the try that reports submission
//    failure (audit M5), so a failing stats refresh can never announce "Failed to
//    process transaction" for a sale that succeeded and make the cashier re-enter
//    it.
//
// Kept, because the audit says these were already right: the submit button is
// disabled BEFORE the await and on an empty cart, and the collection gate is
// checked before the form is usable rather than after the basket is full.

import { supabase } from './supabaseClient.js';
import { showStatus, formatCurrency } from './utils.js';

import { db } from './db/schema.js';
import { recordSale, getAllEvents } from './db/index.js';
import { getEngine, collectionGate } from './ui/engine-boot.js';
import { messageFor, messageForError, UI_STRINGS } from './ui/engine-messages.js';
import { businessDateOf, foldMoney, formatReceiptNo } from './money/index.js';

/**
 * Escape text destined for an `innerHTML` template (audit M7).
 *
 * Patient and service names were interpolated raw throughout the money paths, and
 * these receipts render in the ADMIN's session — the one account with rights over
 * the ledger. Where a node can be built instead, it is; where a template is
 * clearer, every hole goes through here.
 * @param {*} value
 * @returns {string}
 */
function escapeHtml(value) {
    return String(value == null ? '' : value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

/**
 * Coerce a price-list value to whole FCFA, or `null` when it is not a whole
 * number.
 *
 * XAF has no subunit (invariant 5) and `drugs.price` / `revenue_types.amount` are
 * `int` since migration 0009. A non-integer here means the cache predates that
 * migration or the row is corrupt — and REFUSING beats rounding, which is audit M2
 * (three lines of 33.33 printing as 33+33+33 under a total of 100).
 * @param {*} value
 * @returns {number|null}
 */
function toWholeFcfa(value) {
    const n = typeof value === 'number' ? value : Number(String(value ?? '').trim());
    if (!Number.isFinite(n) || !Number.isInteger(n) || n < 0) return null;
    return n;
}

/**
 * Groupe les milliers, et RIEN d'autre.
 *
 * `utils.formatCurrency` (fr-CM → « 1 500 FCFA ») est laissé intact : d'autres
 * écrans et les tests s'appuient dessus. Les écrans de caisse composent
 * eux-mêmes l'unité, parce que la maquette la veut tantôt en suffixe atténué
 * (`<span>`), tantôt collée au nombre, tantôt absente (lignes du reçu).
 */
const fmtNo = new Intl.NumberFormat('fr-FR');

/**
 * Catégorie → libellé français. LA MÊME table que les onglets de filtre
 * (index.html `#service-category-tabs`) : deux orthographes pour une même
 * catégorie se lisent comme deux catégories.
 */
// Les sections de la grille tarifaire CMAO 2025 (+ Haut Standing 2022). Les
// onglets de l'écran (index.html) et le <select> du formulaire admin portent
// les MÊMES slugs — un slug inconnu retombe sur « Autres » sans disparaître.
const CATEGORY_LABELS = {
    accueil: 'Accueil',
    maternite: 'Maternité',
    chirurgie: 'Chirurgie',
    stomatologie: 'Stomatologie',
    laboratoire: 'Laboratoire',
    morgue: 'Morgue',
    haut_standing: 'Haut Standing',
    autre: 'Autres'
};

/**
 * Feuille de style du document d'impression (spéc. §10).
 *
 * Autonome — la fenêtre pop-up ne partage aucune feuille avec l'application, à
 * l'exception de `css/fonts.css` que le document lie lui-même pour Poppins.
 * TOUT est en noir pur : une tête thermique n'a pas de gris, un #616161 sort
 * tramé et sale. Le bloc est volontairement d'un seul tenant pour qu'un
 * `[data-paper="58"]` puisse un jour s'y ajouter sans le disperser.
 */
const PRINT_CSS = `
/* Pas de "size": "80mm auto" est invalide (une longueur ne se combine pas avec
   auto) et Chromium jette alors toute la déclaration. Sur un rouleau thermique
   c'est le pilote qui fixe le format ; le corps porte la largeur de 80mm. */
@page { margin: 0; }
* { box-sizing: border-box; }
html, body { margin: 0; padding: 0; background: #fff; }
body {
    width: 80mm;
    color: #000;
    font-family: 'Inter', system-ui, -apple-system, sans-serif;
    font-size: 12px;
    line-height: 1.45;
    font-variant-numeric: tabular-nums;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
}
.receipt-thermal { width: 80mm; padding: 5mm 4mm 3mm; color: #000; text-align: left; }
.rt-head { text-align: center; }
.rt-mark { width: 14mm; height: 14mm; object-fit: contain; display: block; margin: 0 auto 4px; }
.rt-brand { font-family: 'Poppins', sans-serif; font-size: 16px; font-weight: 700; letter-spacing: 1.2px; line-height: 1.2; }
.rt-line { font-size: 11px; }
.rt-head .rt-line:first-of-type { margin-top: 3px; }
.rt-rule { border-top: 1px dashed #000; margin: 9px 0; }
.rt-id { text-align: center; }
.rt-doctype { font-size: 11px; font-weight: 700; letter-spacing: 0.14em; }
.rt-number { font-family: 'Poppins', sans-serif; font-size: 19px; font-weight: 700; letter-spacing: 1px; margin-top: 2px; }
.rt-meta { display: flex; flex-direction: column; gap: 2px; }
.rt-row { display: flex; justify-content: space-between; gap: 8px; }
.rt-row > span:last-child { text-align: right; }
.rt-row--strong > span:last-child { font-weight: 700; }
.rt-cols { display: flex; justify-content: space-between; font-size: 10px; font-weight: 700; letter-spacing: 0.1em; margin-bottom: 6px; }
.rt-items { display: flex; flex-direction: column; gap: 7px; }
.rt-item-name { font-weight: 700; line-height: 1.3; }
.rt-item-line { display: flex; justify-content: space-between; gap: 8px; }
.rt-solid { border-top: 1px solid #000; margin: 9px 0; }
.rt-total { display: flex; justify-content: space-between; align-items: baseline; gap: 8px; font-family: 'Poppins', sans-serif; font-size: 17px; font-weight: 700; }
.rt-double { height: 2px; border-top: 1px solid #000; border-bottom: 1px solid #000; margin-top: 3px; }
.rt-thanks { text-align: center; margin-top: 11px; font-size: 11px; line-height: 1.5; }
.rt-keep { text-align: center; font-size: 10px; letter-spacing: 0.06em; }
.rt-note { border: 1px solid #000; padding: 5px 6px; margin-top: 9px; font-size: 11px; line-height: 1.4; text-align: center; }
/* Le bord déchiré est un ornement d'aperçu. Le rouleau se coupe tout seul. */
.rt-zigzag { display: none; }
`;

export const CashierATransactions = {
    // Cache DOM elements
    elements: {},
    selectedServices: [],
    currentTotal: 0,
    allRevenueTypes: [], // Store all revenue types for filtering
    currentCategory: 'all',
    /** Latest collection-gate verdict. `null` until the first check. */
    collectionState: null,
    /** True while a committed receipt is on screen — the modal is then locked. */
    receiptCommitted: false,

    // Initialize the module
    init() {
        // Cache elements
        this.elements = {
            // Main form elements
            transactionForm: document.getElementById('cashier-a-transaction-form'),
            patientName: document.getElementById('patient-name'),
            patientId: document.getElementById('patient-id'),
            patientAge: document.getElementById('patient-age'),
            patientSex: document.getElementById('patient-sex'),
            serviceSelect: document.getElementById('service-select'),
            addServiceBtn: document.getElementById('add-service-btn'),
            selectedServicesList: document.getElementById('selected-services-list'),
            totalAmount: document.getElementById('total-amount'),
            submitBtn: document.getElementById('submit-transaction-btn'),
            clearBtn: document.getElementById('clear-transaction-btn'),
            cartCount: document.getElementById('cart-count-a'),
            printPreviewBtn: document.getElementById('print-preview-btn-a'),

            // New grid elements
            servicesGrid: document.getElementById('services-grid'),
            serviceSearch: document.getElementById('service-search'),
            categoryTabs: document.getElementById('service-category-tabs'),
            servicesLoading: document.getElementById('services-loading'),

            // Mini stats
            todayCount: document.getElementById('cashier-a-today-count'),
            todayTotal: document.getElementById('cashier-a-today-total'),
            todayPending: document.getElementById('cashier-a-today-pending'),
            servicesCount: document.getElementById('cashier-a-services-count'),

            // Collection gate banner
            gateBanner: document.getElementById('cashier-a-gate-banner'),

            // Receipt modal
            receiptModal: document.getElementById('transaction-receipt-modal'),
            receiptContent: document.getElementById('transaction-receipt-content'),
            printReceiptBtn: document.getElementById('print-transaction-receipt'),
            closeReceiptBtn: document.getElementById('close-transaction-receipt'),
            newTransactionBtn: document.getElementById('new-transaction-btn')
        };

        // Set up event listeners
        this.setupEventListeners();

        // Load revenue types into select and grid
        this.loadRevenueTypes();

        // Load mini stats
        this.loadMiniStats();

        // Ask the collection gate BEFORE the desk is usable (spec §7). A cashier
        // who fills a basket and only then learns the device cannot collect has
        // already lost the queue behind them.
        void this.refreshGate();

        console.log('CashierATransactions module initialized');
    },

    // Set up all event listeners
    setupEventListeners() {
        // Form actions
        this.elements.addServiceBtn?.addEventListener('click', () => this.addService());
        this.elements.clearBtn?.addEventListener('click', () => this.clearTransaction());
        this.elements.transactionForm?.addEventListener('submit', (e) => this.handleSubmit(e));

        // Allow Enter key to add service
        this.elements.serviceSelect?.addEventListener('keypress', (e) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                this.addService();
            }
        });

        // Search functionality
        this.elements.serviceSearch?.addEventListener('input', (e) => {
            this.filterServices(e.target.value, this.currentCategory);
        });

        // Category tabs
        this.elements.categoryTabs?.addEventListener('click', (e) => {
            if (e.target.classList.contains('category-tab')) {
                // Update active tab
                this.elements.categoryTabs.querySelectorAll('.category-tab').forEach(tab => {
                    tab.classList.remove('active');
                });
                e.target.classList.add('active');

                // Filter services
                this.currentCategory = e.target.dataset.category;
                const searchValue = this.elements.serviceSearch?.value || '';
                this.filterServices(searchValue, this.currentCategory);
            }
        });

        // Receipt modal actions
        this.elements.printReceiptBtn?.addEventListener('click', () => this.printReceipt());
        this.elements.closeReceiptBtn?.addEventListener('click', () => this.closeReceiptModal());
        this.elements.newTransactionBtn?.addEventListener('click', () => this.startNewTransaction());

        // Print preview button
        this.elements.printPreviewBtn?.addEventListener('click', () => this.showReceiptPreview());

        // Modal backdrop click — IGNORED for a committed receipt (audit H3).
        this.elements.receiptModal?.addEventListener('click', (e) => {
            if (e.target === this.elements.receiptModal) this.closeReceiptModal();
        });
    },

    // ---- collection gate ----------------------------------------------------

    /**
     * Re-read the collection gate and reflect it in the UI.
     *
     * Order is spec §7's: durability, identity, receipt supply. A refusal disables
     * the submit button and states the reason in French — never a silent no-op
     * that leaves the cashier clicking.
     * @returns {Promise<{allowed:boolean, reason?:string}>}
     */
    async refreshGate() {
        let gate;
        try {
            gate = await collectionGate();
        } catch (error) {
            gate = { allowed: false, reason: 'engine-not-ready' };
            console.error('collection gate failed:', error);
        }
        this.collectionState = gate;

        const banner = this.elements.gateBanner;
        if (banner) {
            const slot = banner.querySelector('.alert-message') || banner;
            slot.textContent = gate.allowed ? '' : messageFor(gate.reason);
            banner.classList.toggle('hidden', gate.allowed);
        }

        this.updateTotal();
        return gate;
    },

    // Load revenue types into select dropdown and grid
    async loadRevenueTypes() {
        try {
            // Try to get from Supabase first
            const { data, error } = await supabase
                .from('revenue_types')
                .select('*')
                .order('name');

            if (!error && data) {
                // A deactivated service stays resolvable on an old receipt but is
                // not offered for sale (spec §3.3: removal is DEACTIVATION).
                this.allRevenueTypes = data.filter(t => t.is_active !== false);
                this.populateServiceSelect(this.allRevenueTypes);
                this.populateServicesGrid(this.allRevenueTypes);
                // `bulkPut`, correctly idempotent — the audit credits this already.
                await db.revenueTypes.bulkPut(data);
                if (this.elements.servicesCount) {
                    this.elements.servicesCount.textContent = this.allRevenueTypes.length;
                }
            } else {
                // Fallback to the local cache.
                const offlineData = (await db.revenueTypes.toArray()).filter(t => t.is_active !== false);
                this.allRevenueTypes = offlineData;
                this.populateServiceSelect(offlineData);
                this.populateServicesGrid(offlineData);
                if (this.elements.servicesCount) {
                    this.elements.servicesCount.textContent = offlineData.length;
                }
            }
        } catch (error) {
            console.error('Error loading revenue types:', error);
            showStatus('Échec du chargement des services', true);
        }
    },

    /**
     * Today's régie figures for this desk, folded from the local ledger mirror.
     *
     * Reads the SAME events as the dashboard and the reports through the SAME
     * fold, so no two screens can disagree (audit H2) — and unsent sales are
     * included with the spec §9 caveat rather than reading as zero (audit H6).
     */
    async loadMiniStats() {
        try {
            const today = businessDateOf(new Date().toISOString());
            const events = await getAllEvents(db);
            const todayEvents = events.filter(e => e && e.business_date === today);
            const fold = foldMoney(todayEvents);
            const regie = fold.byChannel.regie || { net: 0, saleCount: 0 };

            // How much of that is still on this device only.
            const unsentIds = new Set((await db.table('outbox').toCollection().primaryKeys()));
            const unsent = todayEvents.filter(e => unsentIds.has(e.id) && e.channel === 'regie');

            if (this.elements.todayCount) {
                this.elements.todayCount.textContent = regie.saleCount;
            }
            if (this.elements.todayTotal) {
                // Un nombre issu du repli, jamais du texte saisi : l'unité est
                // un suffixe atténué, comme sur les autres chiffres-clés.
                this.elements.todayTotal.innerHTML =
                    `${fmtNo.format(regie.net)} <span class="stats-cell-unit">F CFA</span>`;
            }
            if (this.elements.todayPending) {
                const text = unsent.length > 0
                    ? `${unsent.length} vente(s) non encore synchronisée(s) sont incluses dans ce montant`
                    : '';
                this.elements.todayPending.textContent = text;
                this.elements.todayPending.classList.toggle('hidden', text === '');
            }
        } catch (error) {
            console.error('Error folding mini stats:', error);
        }
    },

    // Populate the services grid with clickable cards
    populateServicesGrid(revenueTypes) {
        if (!this.elements.servicesGrid) return;

        // Hide loading message
        if (this.elements.servicesLoading) {
            this.elements.servicesLoading.style.display = 'none';
        }

        this.elements.servicesGrid.innerHTML = '';

        if (revenueTypes.length === 0) {
            // Direct `<p>` de la grille : `.items-grid > p` lui garde la largeur
            // de la grille et le centre, plutôt que de laisser un vide muet.
            this.elements.servicesGrid.innerHTML = '<p>Aucun service trouvé</p>';
            return;
        }

        revenueTypes.forEach(type => {
            const card = document.createElement('div');
            card.className = 'item-card';
            card.dataset.id = type.id;
            card.dataset.name = type.name;
            card.dataset.amount = type.amount;
            card.dataset.category = type.category || 'autre';

            // La catégorie en toutes lettres remplace l'ancienne pastille émoji :
            // il fallait deviner qu'un microscope voulait dire « laboratoire ».
            const categoryLabel = CATEGORY_LABELS[type.category] || CATEGORY_LABELS.autre;

            // Escaped: a service name is admin-supplied text (audit M7).
            card.innerHTML = `
                <div class="item-card-cat">${escapeHtml(categoryLabel)}</div>
                <div class="item-card-name">${escapeHtml(type.name)}</div>
                <div class="item-card-foot">
                    <div>
                        <div class="item-card-price">${fmtNo.format(type.amount)} F CFA</div>
                    </div>
                    <button class="item-card-add" type="button" aria-label="Ajouter">
                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.2" d="M12 5v14M5 12h14"/>
                        </svg>
                    </button>
                </div>
            `;

            // Click on card to add service
            card.addEventListener('click', (e) => {
                if (!e.target.classList.contains('item-card-add')) {
                    this.addServiceFromCard(type);
                }
            });

            // Click on add button
            card.querySelector('.item-card-add').addEventListener('click', (e) => {
                e.stopPropagation();
                this.addServiceFromCard(type);
            });

            this.elements.servicesGrid.appendChild(card);
        });
    },

    // Filter services based on search and category
    filterServices(searchTerm, category) {
        let filtered = this.allRevenueTypes;

        // Filter by category. A slug the label table does not know (or a missing
        // one) folds into « autre », so the tab set and the fallback label agree
        // on where such an item lives — it must never be reachable only via « Tous ».
        if (category && category !== 'all') {
            filtered = filtered.filter(type =>
                (CATEGORY_LABELS[type.category] ? type.category : 'autre') === category
            );
        }

        // Filter by search term. Plain substring matching — no `new RegExp` on user
        // input anywhere in this file (audit M6).
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(type =>
                String(type.name || '').toLowerCase().includes(term)
            );
        }

        this.populateServicesGrid(filtered);
    },

    // Add service from grid card
    addServiceFromCard(type) {
        const amount = toWholeFcfa(type.amount);
        if (amount === null) {
            showStatus(`Prix invalide pour « ${type.name} » — rechargez la liste des services.`, true);
            return;
        }

        const service = { id: type.id, name: type.name, amount, quantity: 1 };

        // Check if already added
        const existingIndex = this.selectedServices.findIndex(s => s.id === service.id);
        if (existingIndex > -1) {
            // Increment quantity
            this.selectedServices[existingIndex].quantity += 1;
        } else {
            // Add new service
            this.selectedServices.push(service);
        }

        // Update display
        this.updateSelectedServicesList();
        this.updateTotal();

        // Visual feedback
        showStatus(`${type.name} ajouté`, false);
    },

    // Populate the service select dropdown
    populateServiceSelect(revenueTypes) {
        if (!this.elements.serviceSelect) return;
        this.elements.serviceSelect.innerHTML = '<option value="">Sélectionner un service…</option>';

        // Group by category
        const grouped = revenueTypes.reduce((acc, type) => {
            if (!acc[type.category]) acc[type.category] = [];
            acc[type.category].push(type);
            return acc;
        }, {});

        // Add optgroups
        Object.entries(grouped).forEach(([category, types]) => {
            const optgroup = document.createElement('optgroup');
            // La même table que les onglets et les cartes. Sans elle ce menu
            // affichait « LABORATORY » et « SURGERY » — en anglais, dans une
            // interface française.
            optgroup.label = CATEGORY_LABELS[category] || CATEGORY_LABELS.autre;

            types.forEach(type => {
                const option = document.createElement('option');
                option.value = type.id;
                option.textContent = `${type.name} — ${fmtNo.format(type.amount)} F CFA`;
                option.dataset.name = type.name;
                option.dataset.amount = type.amount;
                optgroup.appendChild(option);
            });

            this.elements.serviceSelect.appendChild(optgroup);
        });
    },

    // Add selected service to the list
    addService() {
        const select = this.elements.serviceSelect;
        const selectedOption = select.options[select.selectedIndex];

        if (!selectedOption || !selectedOption.value) {
            showStatus('Veuillez sélectionner un service', true);
            return;
        }

        const amount = toWholeFcfa(selectedOption.dataset.amount);
        if (amount === null) {
            showStatus('Prix invalide pour ce service — rechargez la liste des services.', true);
            return;
        }

        const service = {
            id: selectedOption.value,
            name: selectedOption.dataset.name,
            amount,
            quantity: 1
        };

        // Check if already added
        const existingIndex = this.selectedServices.findIndex(s => s.id === service.id);
        if (existingIndex > -1) {
            this.selectedServices[existingIndex].quantity += 1;
        } else {
            this.selectedServices.push(service);
        }

        // Reset select
        select.value = '';

        // Update display
        this.updateSelectedServicesList();
        this.updateTotal();
    },

    // Update the display of selected services
    updateSelectedServicesList() {
        if (!this.elements.selectedServicesList) return;

        this.elements.selectedServicesList.innerHTML = '';

        // Update cart count badge. Le zéro reste écrit — un compteur absent se
        // lit comme un widget cassé, pas comme « rien ». C'est la couleur qui
        // change, pas la présence du nombre.
        if (this.elements.cartCount) {
            this.elements.cartCount.textContent = this.selectedServices.length;
            this.elements.cartCount.classList.toggle('has-items', this.selectedServices.length > 0);
        }

        if (this.selectedServices.length === 0) {
            // `.cart-empty` doit rester l'UNIQUE enfant du corps du panier :
            // c'est son `margin:auto` qui le centre. Il garde la taille du
            // panier plein et dit quoi faire (invariant « états vides »).
            this.elements.selectedServicesList.innerHTML = `
                <div class="cart-empty">
                    <div class="cart-empty-icon">
                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"/>
                        </svg>
                    </div>
                    <p class="cart-empty-text">Aucun service sélectionné</p>
                    <p class="cart-empty-hint">Choisissez une prestation dans le catalogue pour démarrer l'encaissement.</p>
                </div>
            `;
            return;
        }

        this.selectedServices.forEach((service, index) => {
            const serviceDiv = document.createElement('div');
            serviceDiv.className = 'cart-item';

            // La ligne de détail porte le prix UNITAIRE et la quantité : sans la
            // quantité, deux consultations à 2 000 affichent « 4 000 » sous un
            // prix unitaire de 2 000 et ressemblent à une erreur de caisse.
            serviceDiv.innerHTML = `
                <div class="cart-item-info">
                    <span class="cart-item-name">${escapeHtml(service.name)}</span>
                    <span class="cart-item-qty">${fmtNo.format(service.quantity)} × ${fmtNo.format(service.amount)} F CFA</span>
                </div>
                <div class="cart-item-actions">
                    <span class="cart-item-price">${fmtNo.format(service.amount * service.quantity)} F CFA</span>
                    <button class="cart-item-remove" type="button" data-index="${index}" aria-label="Supprimer" title="Supprimer">
                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                        </svg>
                    </button>
                </div>
            `;

            // A listener, not an inline `onclick` handler built by interpolation.
            serviceDiv.querySelector('.cart-item-remove')
                ?.addEventListener('click', () => this.removeService(index));

            this.elements.selectedServicesList.appendChild(serviceDiv);
        });
    },

    // Remove a service from the list
    removeService(index) {
        this.selectedServices.splice(index, 1);
        this.updateSelectedServicesList();
        this.updateTotal();
    },

    // Update total amount
    updateTotal() {
        this.currentTotal = this.selectedServices.reduce((total, service) => {
            return total + (service.amount * service.quantity);
        }, 0);

        if (this.elements.totalAmount) {
            this.elements.totalAmount.innerHTML =
                `${fmtNo.format(this.currentTotal)} <span class="cart-total-unit">F CFA</span>`;
        }

        const hasItems = this.selectedServices.length > 0;
        const gateAllows = !this.collectionState || this.collectionState.allowed;

        // Disabled on an empty cart AND while the device may not collect.
        if (this.elements.submitBtn) {
            this.elements.submitBtn.disabled = !hasItems || !gateAllows;
        }

        // Preview stays available even when collection is refused: the cashier
        // still needs the figures for the paper book.
        if (this.elements.printPreviewBtn) {
            this.elements.printPreviewBtn.disabled = !hasItems;
        }
    },

    // Clear the transaction form
    clearTransaction() {
        this.selectedServices = [];
        this.currentTotal = 0;
        this.elements.transactionForm?.reset();
        this.updateSelectedServicesList();
        this.updateTotal();
        // Clear optional fields
        if (this.elements.patientAge) this.elements.patientAge.value = '';
        if (this.elements.patientSex) this.elements.patientSex.value = '';
    },

    /**
     * Project the cart into spec §6 line items.
     *
     * The SPEC shape — `{item_id, item_kind, qty, unit_price, line_total}` — not
     * the legacy `{name, quantity, unit_price, total}`. A line the stock and price
     * folds cannot attribute is audit C4, and `db/ledger-write.js` refuses it at
     * the door rather than half-folding it later.
     *
     * @returns {{item_id:string, item_kind:'revenue_type', name:string, qty:number,
     *   unit_price:number, line_total:number}[]}
     * @throws {Error} With a French message, when a price is not whole FCFA.
     */
    buildLineItems() {
        return this.selectedServices.map(service => {
            const unitPrice = toWholeFcfa(service.amount);
            const qty = toWholeFcfa(service.quantity);
            if (unitPrice === null || qty === null || qty <= 0) {
                throw new Error(
                    `Ligne invalide pour « ${service.name} » : prix ou quantité non entiers. Retirez-la du panier et rechargez la liste des services.`
                );
            }
            return {
                item_id: service.id,
                item_kind: 'revenue_type',
                name: service.name,
                qty,
                unit_price: unitPrice,
                line_total: qty * unitPrice
            };
        });
    },

    // Handle form submission
    async handleSubmit(e) {
        e.preventDefault();

        if (this.selectedServices.length === 0) {
            showStatus('Ajoutez au moins un service', true);
            return;
        }

        const patientName = this.elements.patientName.value.trim();
        if (!patientName) {
            showStatus('Saisissez le nom du patient', true);
            this.elements.patientName.focus();
            return;
        }

        // THE GATE, before anything is minted or written (spec §7).
        const gate = await this.refreshGate();
        if (!gate.allowed) {
            showStatus(messageFor(gate.reason), true);
            return;
        }

        const engine = getEngine();
        if (!engine || !engine.deviceId) {
            showStatus(messageFor('device-not-provisioned'), true);
            return;
        }

        let items;
        try {
            items = this.buildLineItems();
        } catch (error) {
            showStatus(error.message, true);
            return;
        }
        const amount = items.reduce((sum, line) => sum + line.line_total, 0);

        // Disabled BEFORE the await — the audit credits this and it stays.
        this.elements.submitBtn.disabled = true;
        // innerHTML, pas textContent : le bouton contient un <svg> que
        // textContent détruirait définitivement à la première vente.
        const originalLabel = this.elements.submitBtn.innerHTML;
        this.elements.submitBtn.textContent = 'Enregistrement…';

        /** @type {{id:string, receipt_no:number}|null} */
        let saved = null;
        const deviceTime = new Date().toISOString();

        try {
            // ONE call. The UUID is minted HERE, exactly once, before any network
            // attempt, and is NEVER regenerated on retry (invariant 2). The write
            // is local and atomic — ledger mirror + outbox + receipt number in one
            // Dexie transaction — and the sync engine owns everything after it.
            saved = await recordSale(db, {
                channel: 'regie',
                amount,
                items,
                device_id: engine.deviceId,
                device_time: deviceTime,
                business_date: businessDateOf(deviceTime),
                patient_name: patientName,
                book_no: null,
                collected_by: null
            });
        } catch (error) {
            // A REFUSAL IS A REFUSAL. There is no "must be offline" branch here and
            // there must never be one again (audit C1).
            console.error('Sale refused:', error);
            showStatus(messageForError(error), true);
            return;
        } finally {
            this.elements.submitBtn.disabled = false;
            this.elements.submitBtn.innerHTML = originalLabel;
            this.updateTotal();
        }

        // ─── POST-COMMIT ────────────────────────────────────────────────────
        // Everything below is OUTSIDE the try above (audit M5). The money is
        // recorded; nothing here may report the sale as failed.

        const receipt = {
            receiptNo: saved.receipt_no,
            receiptLabel: formatReceiptNo(saved.receipt_no),
            dateIso: deviceTime,
            patient: {
                name: patientName,
                id: this.elements.patientId?.value || '',
                age: this.elements.patientAge?.value || '',
                sex: this.elements.patientSex?.value || ''
            },
            lines: items,
            total: amount,
            committed: true
        };

        // Cart cleared BEFORE the modal opens (audit H3): there is nothing left to
        // re-submit even if the browser does something unexpected with the dialog.
        this.clearTransaction();
        this.showReceipt(receipt);

        showStatus(`Reçu n° ${receipt.receiptLabel} — ${UI_STRINGS.RECEIPT_ISSUED}`, false);

        void this.afterCommit();
    },

    /**
     * Side effects that must never be able to report a committed sale as failed.
     * Each is guarded on its own: a stats refresh that throws is a cosmetic
     * problem, and treating it as a money problem is what made cashiers re-enter
     * sales that had already gone through (audit M5).
     */
    async afterCommit() {
        const engine = getEngine();
        if (engine) {
            try { await engine.kick(); } catch (error) { console.error('sync kick failed:', error); }
        }
        try { await this.loadMiniStats(); } catch (error) { console.error('mini stats failed:', error); }
        try { await this.refreshGate(); } catch (error) { console.error('gate refresh failed:', error); }
        try {
            const userRole = window.AppCore?.getCurrentRole?.();
            if (userRole === 'admin' && window.AppCore?.getDashboardStats) {
                window.AppCore.renderDashboardStats(await window.AppCore.getDashboardStats());
                // The activity feed and the reminders read the ledger too. Without
                // this the tiles move on a sale and the list under them does not,
                // which reads as the sale not having been recorded.
                await window.UI?.refreshDashboardFeeds?.();
            }
        } catch (error) {
            console.error('dashboard refresh failed:', error);
        }
    },

    // Show receipt preview (before submitting)
    showReceiptPreview() {
        if (this.selectedServices.length === 0) {
            showStatus('Aucun service sélectionné', true);
            return;
        }

        let items;
        try {
            items = this.buildLineItems();
        } catch (error) {
            showStatus(error.message, true);
            return;
        }

        this.showReceipt({
            receiptNo: null,
            receiptLabel: 'APERÇU',
            dateIso: new Date().toISOString(),
            patient: {
                name: this.elements.patientName?.value || 'Non spécifié',
                id: this.elements.patientId?.value || '',
                age: this.elements.patientAge?.value || '',
                sex: this.elements.patientSex?.value || ''
            },
            lines: items,
            total: items.reduce((sum, line) => sum + line.line_total, 0),
            committed: false
        });
    },

    /**
     * Render the receipt.
     *
     * A COMMITTED receipt locks the modal: the ✕ is hidden and the backdrop does
     * nothing, so the only way out is « Nouvelle Transaction », which clears
     * everything (audit H3). A PREVIEW stays freely dismissible.
     *
     * @param {object} receipt Normalised receipt view-model.
     */
    showReceipt(receipt) {
        this.receiptCommitted = receipt.committed === true;

        // Deux lignes par article : le nom sur toute la largeur, puis
        // « qté × prix unitaire » à gauche et le total de ligne à droite. Les
        // nombres du corps du reçu sont NUS — seul le TOTAL porte « F CFA »,
        // sur un rouleau de 80 mm chaque répétition coûte de la largeur.
        const items = receipt.lines.map(line => `
                    <div class="rt-item">
                        <div class="rt-item-name">${escapeHtml(line.name)}</div>
                        <div class="rt-item-line">
                            <span>${fmtNo.format(line.qty)} × ${fmtNo.format(line.unit_price)}</span>
                            <span>${fmtNo.format(line.line_total)}</span>
                        </div>
                    </div>`).join('');

        // Une ligne de méta absente vaut mieux qu'une ligne vide : le reçu ne
        // promet pas un champ qu'il n'a pas.
        const metaRow = (label, value, strong) => value
            ? `<div class="rt-row${strong ? ' rt-row--strong' : ''}"><span>${escapeHtml(label)}</span><span>${escapeHtml(value)}</span></div>`
            : '';

        const sexLabel = receipt.patient.sex === 'M' ? 'Masculin'
            : receipt.patient.sex === 'F' ? 'Féminin' : '';
        const ageLabel = receipt.patient.age ? `${receipt.patient.age} ans` : '';
        const ageSex = [ageLabel, sexLabel].filter(Boolean).join(' · ');

        const paperBookNote = receipt.committed
            ? `<div class="rt-note">${escapeHtml(UI_STRINGS.RECEIPT_ISSUED)}</div>`
            : '';

        const receiptHtml = `
            <div class="receipt-thermal" id="printable-transaction-receipt">
                <div class="rt-head">
                    <img class="rt-mark" src="assets/logo-mark-black.png" alt="">
                    <div class="rt-brand">CMA OMBESSA</div>
                    <div class="rt-line">Centre Médical d'Arrondissement</div>
                    <div class="rt-line">Tél: +237 695 615 932</div>
                </div>
                <div class="rt-rule"></div>
                <div class="rt-id">
                    <div class="rt-doctype">REÇU — RÉGIE</div>
                    <div class="rt-number">${escapeHtml(receipt.receiptLabel)}</div>
                </div>
                <div class="rt-rule"></div>
                <div class="rt-meta">
                    ${metaRow('Date', new Date(receipt.dateIso).toLocaleString('fr-CM'), false)}
                    ${metaRow('Patient', receipt.patient.name, true)}
                    ${metaRow('Dossier', receipt.patient.id, false)}
                    ${metaRow('Âge / Sexe', ageSex, false)}
                </div>
                <div class="rt-rule"></div>
                <div class="rt-cols"><span>SERVICE</span><span>MONTANT</span></div>
                <div class="rt-items">${items}
                </div>
                <div class="rt-solid"></div>
                <div class="rt-total"><span>TOTAL</span><span>${fmtNo.format(receipt.total)} F CFA</span></div>
                <div class="rt-double"></div>
                <div class="rt-thanks">Merci de votre visite.<br>Prompt rétablissement.</div>
                <div class="rt-rule"></div>
                <div class="rt-keep">CONSERVEZ CE REÇU</div>
                ${paperBookNote}
            </div>`;

        // Le bord déchiré n'existe que dans l'aperçu — `printReceipt()` ne
        // récupère que `#printable-transaction-receipt`, jamais le zigzag.
        this.elements.receiptContent.innerHTML = `${receiptHtml}
            <div class="rt-zigzag"></div>`;

        // Lock the exits on a committed receipt.
        if (this.elements.closeReceiptBtn) {
            this.elements.closeReceiptBtn.classList.toggle('hidden', this.receiptCommitted);
        }
        this.elements.receiptModal.classList.remove('hidden');
    },

    // Print receipt
    printReceipt() {
        const printContent = document.getElementById('printable-transaction-receipt');
        if (!printContent) return;
        const printWindow = window.open('', '', 'height=600,width=400');
        if (!printWindow) {
            showStatus('Impression bloquée par le navigateur — autorisez les fenêtres pop-up.', true);
            return;
        }

        // UN document complet, écrit en une fois. `css/fonts.css` est lié en
        // relatif : la pop-up hérite de l'URL de base de l'ouvreur, la feuille
        // est de même origine et déjà en cache du service worker — aucun CDN.
        // Poppins doit survivre à l'impression : c'est la police de l'en-tête,
        // du numéro de reçu et du TOTAL.
        //
        // `outerHTML`, pas `innerHTML` : la classe `receipt-thermal` porte la
        // largeur de 80 mm et les marges du rouleau.
        printWindow.document.write(`<!doctype html>
<html lang="fr">
<head>
<meta charset="utf-8">
<title>Reçu — Régie</title>
<link rel="stylesheet" href="css/fonts.css">
<style>${PRINT_CSS}</style>
</head>
<body>${printContent.outerHTML}</body>
</html>`);

        printWindow.document.close();

        // Le logo et Poppins arrivent par le réseau ; `print()` appelé dans la
        // foulée de `document.close()` court après eux. On laisse le document
        // finir, puis les polices — et un délai de garde imprime quand même si
        // une ressource traîne : un reçu sans logo vaut mieux qu'un reçu qui ne
        // sort pas. `readyState` est testé d'abord pour que le cas normal
        // n'attende rien du tout.
        let printed = false;
        const doPrint = () => {
            if (printed) return;
            printed = true;
            try {
                printWindow.focus();
                printWindow.print();
            } catch (error) {
                console.error('print failed:', error);
            }
        };
        const whenLoaded = () => {
            const fonts = printWindow.document.fonts;
            if (fonts && fonts.ready) fonts.ready.then(doPrint, doPrint);
            else doPrint();
        };

        if (printWindow.document.readyState === 'complete') whenLoaded();
        else printWindow.addEventListener('load', whenLoaded, { once: true });
        printWindow.setTimeout(doPrint, 3000);

        // NOT `printWindow.close()` here: closing immediately after `print()`
        // cancels the job on several browsers (audit, Low).
    },

    /**
     * Close the modal — refused while a committed receipt is showing.
     *
     * This is the other half of audit H3: dismissing a committed receipt used to
     * leave the cart loaded, and one more click booked a second identical sale.
     */
    closeReceiptModal() {
        if (this.receiptCommitted) {
            showStatus('Utilisez « Nouvelle Transaction » pour continuer.', false);
            return;
        }
        this.elements.receiptModal.classList.add('hidden');
    },

    // Start new transaction
    startNewTransaction() {
        this.receiptCommitted = false;
        if (this.elements.closeReceiptBtn) this.elements.closeReceiptBtn.classList.remove('hidden');
        this.elements.receiptModal.classList.add('hidden');
        this.clearTransaction();
        this.elements.patientName?.focus();
        void this.refreshGate();
    }
};

// Make CashierATransactions available globally
window.CashierATransactions = CashierATransactions;
